import React, { useEffect, useState, useCallback } from 'react';
import { FiRefreshCw, FiCheckCircle } from 'react-icons/fi';
import { generateRemittance, listRemittances, markRemittancePaid } from './payrollApi';

const money = (n: any) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ymd = (v: any) => (v ? String(v).slice(0, 10) : '');

export default function RemittancesTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-based
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows(await listRemittances(year)); }
    catch (e: any) { setError(e.message || 'Could not load'); }
    finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const monthRange = () => {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    const f = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: f(start), end: f(end) };
  };

  const doGenerate = async () => {
    setBusy('gen'); setError('');
    try { const { start, end } = monthRange(); await generateRemittance(start, end); await load(); }
    catch (e: any) { setError(e.message || 'Could not generate remittance'); }
    finally { setBusy(''); }
  };
  const markPaid = async (r: any) => {
    const conf = window.prompt('Mark this remittance PAID.\nEnter a CRA confirmation number (optional):', '');
    if (conf === null) return;
    setBusy('paid' + r.pay_remittance_id);
    try { await markRemittancePaid(r.pay_remittance_id, new Date().toISOString().slice(0, 10), conf || undefined); await load(); }
    catch (e: any) { setError(e.message || 'Could not update'); }
    finally { setBusy(''); }
  };

  return (
    <div>
      <div className="pay-toolbar">
        <div className="pay-field" style={{ maxWidth: 110 }}><label>Year</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[year + 1, year, year - 1, year - 2].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="pay-field" style={{ maxWidth: 120 }}><label>Month</label>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
        </div>
        <span className="spacer" />
        <button className="pay-btn" onClick={doGenerate} disabled={!!busy}><FiRefreshCw /> {busy === 'gen' ? 'Calculating…' : `Generate ${MONTHS[month]} ${year} remittance`}</button>
      </div>
      {error && <div className="pay-error">{error}</div>}
      <div className="pay-note" style={{ marginBottom: 'var(--spacing-md)' }}>
        Source deductions (PD7A) rolled up from <strong>approved/posted</strong> pay runs in the period. Regular-remitter due date = the 15th of the following month. Verify your remitter type and schedule with CRA.
      </div>
      <div className="pay-card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? <div className="pay-empty">Loading…</div>
          : rows.length === 0 ? <div className="pay-empty">No remittances yet for {year}. Pick a month and generate.</div>
          : (
          <table className="pay-table">
            <thead><tr>
              <th>Period</th><th>Due</th><th className="num">CPP (ee+er)</th><th className="num">EI (ee+er)</th><th className="num">Income tax</th>
              <th className="num">Total</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.pay_remittance_id}>
                  <td>{ymd(r.period_start)} → {ymd(r.period_end)}</td>
                  <td>{ymd(r.due_date)}</td>
                  <td className="num">{money(Number(r.cpp_employee) + Number(r.cpp_employer))}</td>
                  <td className="num">{money(Number(r.ei_employee) + Number(r.ei_employer))}</td>
                  <td className="num">{money(r.income_tax)}</td>
                  <td className="num pos">{money(r.total_remittance)}</td>
                  <td><span className={`pay-badge ${r.status === 'paid' ? 'posted' : 'calculated'}`}>{r.status}{r.confirmation_no ? ` · ${r.confirmation_no}` : ''}</span></td>
                  <td className="num">{r.status !== 'paid' && <button className="pay-btn ghost sm" onClick={() => markPaid(r)} disabled={!!busy}><FiCheckCircle /> Mark paid</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
